import { clearStoredSession, getStoredToken } from './tokenStore'
import type { ApiErrorBody, ApiSuccess } from '@/types/api'

/** Base path for the API. The Vite dev server proxies `/api` to the backend. */
const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api'

/** Fired when the API rejects the stored session, so the app can sign the user out. */
export const UNAUTHORIZED_EVENT = 'aims:unauthorized'

/** Codes the API uses when the bearer session itself is unusable. */
const SESSION_FAILURE_CODES = new Set(['MISSING_TOKEN', 'INVALID_SESSION'])

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: unknown

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }

  /** True when the caller is authenticated but lacks the required permission. */
  get isForbidden(): boolean {
    return this.status === 403
  }

  /** True when the API could not be reached at all (network / dev proxy). */
  get isNetworkError(): boolean {
    return this.status === 0
  }
}

type QueryValue = string | number | boolean | null | undefined

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  /** JSON body, serialised automatically. */
  body?: unknown
  /** Query parameters; blank (`''`, null, undefined) values are dropped. */
  query?: Record<string, QueryValue>
  signal?: AbortSignal
}

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const base = BASE_URL.replace(/\/+$/, '')
  const suffix = path.startsWith('/') ? path : `/${path}`
  const url = `${base}${suffix}`
  if (!query) return url

  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    params.append(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

/**
 * Perform a JSON request against the AIMS API.
 *
 * Unwraps the `{ success, message, data }` envelope and returns `data`, or
 * throws {@link ApiError} for any non-2xx response or transport failure.
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal } = options

  const headers: Record<string, string> = { Accept: 'application/json' }
  const token = getStoredToken()
  if (token) headers.Authorization = `Bearer ${token}`
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData
  // Let the browser set the multipart boundary for FormData bodies.
  if (body !== undefined && !isFormData) headers['Content-Type'] = 'application/json'

  let response: Response
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      signal,
      body: body === undefined ? undefined : isFormData ? (body as FormData) : JSON.stringify(body),
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new ApiError(0, 'NETWORK_ERROR', 'The server could not be reached. Please try again.')
  }

  const text = await response.text()
  let payload: unknown = null
  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = null
    }
  }

  if (!response.ok) {
    const errorBody = payload as ApiErrorBody | null
    const code = errorBody?.error?.code ?? 'REQUEST_FAILED'
    const message = errorBody?.error?.message ?? `Request failed (${response.status}).`
    const details = errorBody?.error?.details

    // A rejected *session* means the app should sign out; a failed sign-in does not.
    if (response.status === 401 && SESSION_FAILURE_CODES.has(code)) {
      clearStoredSession()
      if (typeof window !== 'undefined') window.dispatchEvent(new Event(UNAUTHORIZED_EVENT))
    }

    throw new ApiError(response.status, code, message, details)
  }

  const envelope = payload as ApiSuccess<T> | null
  if (envelope && typeof envelope === 'object' && 'data' in envelope) return envelope.data
  return payload as T
}

/**
 * Fetch a binary response (e.g. a generated PDF) with the stored bearer
 * session. Mirrors {@link apiRequest}'s error handling so callers still get an
 * {@link ApiError} with the API's code/message.
 */
export async function apiBlobRequest(
  path: string,
  options: { signal?: AbortSignal; query?: Record<string, QueryValue>; accept?: string } = {},
): Promise<Blob> {
  const headers: Record<string, string> = { Accept: options.accept ?? 'application/pdf' }
  const token = getStoredToken()
  if (token) headers.Authorization = `Bearer ${token}`

  let response: Response
  try {
    response = await fetch(buildUrl(path, options.query), { headers, signal: options.signal })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new ApiError(0, 'NETWORK_ERROR', 'The server could not be reached. Please try again.')
  }

  if (!response.ok) {
    let payload: unknown = null
    try {
      payload = await response.json()
    } catch {
      payload = null
    }
    const errorBody = payload as ApiErrorBody | null
    const code = errorBody?.error?.code ?? 'REQUEST_FAILED'
    const message = errorBody?.error?.message ?? `Request failed (${response.status}).`
    if (response.status === 401 && SESSION_FAILURE_CODES.has(code)) {
      clearStoredSession()
      if (typeof window !== 'undefined') window.dispatchEvent(new Event(UNAUTHORIZED_EVENT))
    }
    throw new ApiError(response.status, code, message, errorBody?.error?.details)
  }

  return response.blob()
}

/**
 * Fetch a binary resource (such as a stored document) with the session attached.
 *
 * The document endpoint streams bytes and requires the bearer token, so an
 * `<img src>` cannot be used directly — callers turn the returned blob into an
 * object URL instead.
 */
export async function apiFetchBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  const headers: Record<string, string> = { Accept: '*/*' }
  const token = getStoredToken()
  if (token) headers.Authorization = `Bearer ${token}`

  let response: Response
  try {
    response = await fetch(buildUrl(path), { headers, signal })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new ApiError(0, 'NETWORK_ERROR', 'The server could not be reached. Please try again.')
  }

  if (!response.ok) {
    let code = 'REQUEST_FAILED'
    let message = `Request failed (${response.status}).`
    let details: unknown
    try {
      const body = (await response.json()) as ApiErrorBody
      code = body?.error?.code ?? code
      message = body?.error?.message ?? message
      details = body?.error?.details
    } catch {
      // The failure body was not JSON; keep the generic message.
    }

    if (response.status === 401 && SESSION_FAILURE_CODES.has(code)) {
      clearStoredSession()
      if (typeof window !== 'undefined') window.dispatchEvent(new Event(UNAUTHORIZED_EVENT))
    }

    throw new ApiError(response.status, code, message, details)
  }

  return response.blob()
}
